<template>
  <div class="faq-section">
    <div class="container">
      <h2 class="faq-title">Frequently Asked Questions</h2>

      <div class="faq-list">
        <div
          class="faq-item"
          v-for="(item, index) in faqList"
          :key="index"
        >
          <div
            class="faq-question"
            @click="toggleFaq(index)"
            :class="{ active: openIndex === index }"
          >
            <span class="question-text">{{ item.question }}</span>
            <span class="icon">
              {{ openIndex === index ? '−' : '+' }}
            </span>
          </div>

          <div class="faq-answer" :class="{ open: openIndex === index }">
            <div class="answer-inner">
              {{ item.answer }}
            </div>
          </div>
        </div>
      </div>

      <div class="faq-image">
        <img src="@/assets/images/faq.jpg" alt="CNC Machining Inspection" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'faq',
  setup() {
    const openIndex = ref(null)
    
    const toggleFaq = (index) => {
      openIndex.value = openIndex.value === index ? null : index
    }
    
    const faqList = ref([
      {
        question: 'What type of design files can you accept?',
        answer:
          'We accept 3D CAD files in STL, STP, STEP, OBJ IGS, or IGES format and 2D drawings(jpg, png, gif, BMP, pdf) with dimensional details and specific manufacturing requirements.'
      },
      {
        question: 'How long does it take to get a pricing quote?',
        answer:
          'We respond within 2 hours and usually provide pricing within 8 hours.'
      },
      {
        question:
          'Can I use my master model to make duplicate copies for vacuum casting or CNC machining?',
        answer:
          'Yes, master models are used to create silicone molds for vacuum casting and can be made from any rigid solid that can withstand heat up to 100 °C. Models used for CNC machining will be 3D scanned to create a CAD/CAM program for production.'
      },
      {
        question: 'How quickly can I get my parts?',
        answer:
          'Simple parts can be completed in as fast as one week depending on complexity and quantity.'
      },
      {
        question: 'How do we protect your data and intellectual property?',
        answer:
          'We respect your intellectual property. None of your information, images, files or likenesses will be shared with any third party, and it will not be used for any advertising, marketing, or promotional use without your explicit written authorization. If you would like to sign NDA agreement, please contact our team for support.'
      },
      {
        question: 'How about the quality guarantee policy?',
        answer:
          'We follow the ISO/IATF standard and guarantee that we will rework the products or refund them if there are any quality problems. The full-dimensional report, material certification, or first article inspection are provided on request before order placement.'
      }
    ])
    
    return {
      openIndex,
      toggleFaq,
      faqList
    }
  }
}
</script>

<style scoped>
.faq-section {
  background-color: #f8f9fc;
  padding: 60px 20px;
}

/* ===== Grid 核心布局 ===== */
.container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto 1fr; /* 分配两行：第一行自适应标题高度，第二行占满剩余空间 */
  column-gap: 40px;
  align-items: start;
}

/* 标题占第一行，第一列 */
.faq-title {
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 30px; 
  color: #1a202c;
  line-height: 1.3;
}

/* 列表占第二行，第一列 */
.faq-list {
  grid-column: 1 / 2;
  grid-row: 2 / 3;
}

/* 图片占第二行，第二列 */
.faq-image {
  grid-column: 2 / 3;
  grid-row: 2 / 3; 
  /* 移除了 margin-top 的硬编码！利用 grid-row，无论屏幕多宽，图片和列表永远绝对齐平 */
}

.faq-image img {
  width: 100%;
  border-radius: 6px;
  display: block;
}

/* ===== FAQ item ===== */
.faq-item {
  border: 1px solid #eaeaea;
  border-bottom: none;
}
.faq-item:last-child {
  border-bottom: 1px solid #eaeaea;
}

/* ===== ⭐ Question 多行对齐核心 ===== */
.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: flex-start; /* 顶部对齐 */
  padding: 18px 20px;
  cursor: pointer;
  background: #fff;
  transition: background 0.2s ease;
  gap: 16px; /* 防止文本过长贴住图标 */
}

.faq-question:hover {
  background: #f5f7fa;
}

.faq-question.active {
  color: #16bc9c;
  border-bottom: 1px solid #eaeaea;
}

.question-text {
  font-size: 17px;
  font-weight: 600;
  line-height: 24px; /* ⭐ 核心：给定明确的像素级单行高度 */
}

.icon {
  font-size: 24px;
  font-weight: 400;
  flex-shrink: 0; 
  
  /* ⭐ 核心：将图标变成一个独立居中的小盒子，高度与文字单行高度(24px)完全一致 */
  height: 24px;
  width: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ===== Answer 动画 ===== */
.faq-answer {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.35s ease;
  background: #fff;
}

.answer-inner {
  overflow: hidden;
  padding: 0 20px;
  color: #4a5568;
  line-height: 1.7;
  font-size: 16px;
}

.faq-answer.open {
  grid-template-rows: 1fr;
}
.faq-answer.open .answer-inner {
  padding: 18px 20px;
}

/* ===== Mobile 响应式 ===== */
@media (max-width: 768px) {
  .container {
    grid-template-columns: 1fr; /* 变成单列 */
    grid-template-rows: auto auto auto;
    row-gap: 0; /* 控制垂直间距 */
  }

  /* 解除网格限制，让它们自然按 DOM 顺序上下排列 */
  .faq-title,
  .faq-list,
  .faq-image {
    grid-column: 1 / -1;
    grid-row: auto;
  }
  
  .faq-image {
    margin-top: 40px; /* 在手机端单列时，给图片上方增加间距 */
  }
}
</style>